import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import { v4 } from 'uuid';

// UI
import { NotificationOverlay, NotificationPayload } from '../components/notification';
type NotificationCreatePayload = Omit<NotificationPayload, 'uniqueId'>;
export type NotificationContextValue = {
  addNotification: (notification: NotificationCreatePayload, uniqueId?: string) => void;
  clearNotifications: () => void;
  removeNotification: (notificationId: string) => void;
  setNotification: (notification: NotificationPayload) => void;
  showUnauthenticatedError: (options?: {
    consoleMessage?: string;
  }) => void;
  showGenericError: (options?: {
    consoleMessage?: string;
    timeout: number;
  }) => void;
};

// # region [rgba(0, 75, 75, 0.1)] --- Provider

interface Props {
  children: React.ReactNode;
}
export const NotificationProvider: React.FC<Props> = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [notifications, setNotifications] = React.useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (notification, t3) => {
      const uniqueId = t3 === undefined ? v4() : t3;
      const notificationToAdd = {
        ...notification,
        ...{
          uniqueId
        }
      };
      setNotifications(prevNotifications => [...prevNotifications, notificationToAdd]);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const addNotification = t2;
  const [markAllForRemoval, setMarkAllForRemoval] = React.useState(false);
  let t3;
  if ($[2] !== notifications.length) {
    t3 = () => {
      if (notifications.length) {
        setMarkAllForRemoval(true);
      }
    };
    $[2] = notifications.length;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const clearNotifications = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = notificationId => {
      setNotifications(prevNotifications_0 => prevNotifications_0.filter(notification_0 => notification_0.uniqueId !== notificationId));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const removeNotification = t4;
  let t5;
  let t6;
  if ($[5] !== markAllForRemoval || $[6] !== notifications.length) {
    t5 = () => {
      if (!notifications.length && markAllForRemoval) {
        setMarkAllForRemoval(false);
      }
    };
    t6 = [notifications.length, markAllForRemoval];
    $[5] = markAllForRemoval;
    $[6] = notifications.length;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  React.useEffect(t5, t6);
  let t7;
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = notification_1 => addNotification(notification_1);
    t8 = () => {
      addNotification({
        title: "User is unauthenticated",
        message: "An authentication error occurred. Please sign in and try again.",
        type: "error",
        timeout: 5000
      });
    };
    t9 = options => {
      addNotification({
        message: "An unexpected error occurred. Please refresh or try again later.",
        title: "An Error Occurred",
        timeout: options?.timeout,
        type: "error"
      });
    };
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[12] !== clearNotifications) {
    t10 = {
      addNotification,
      clearNotifications,
      removeNotification,
      setNotification: t7,
      showUnauthenticatedError: t8,
      showGenericError: t9
    };
    $[12] = clearNotifications;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const contextValue = t10;
  let t11;
  if ($[14] !== markAllForRemoval || $[15] !== notifications) {
    t11 = <NotificationOverlay markAllForRemoval={markAllForRemoval} notifications={notifications} removeNotification={removeNotification} />;
    $[14] = markAllForRemoval;
    $[15] = notifications;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== contextValue || $[19] !== t11) {
    t12 = <NotificationContext.Provider value={contextValue}>{t11}{children}</NotificationContext.Provider>;
    $[17] = children;
    $[18] = contextValue;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};

// #endregion

export const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);
export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};